import classNames from 'classnames';
import React, { useRef } from 'react';
import { Transition, TransitionStatus } from 'react-transition-group';

import { omit, pick, TransitionPropTypeKeys, TransitionTimeouts } from './utils';

const TransitionAny = Transition as unknown as React.ComponentType<Record<string, unknown>>;

export interface FadeProps {
  tag?: React.ElementType;
  baseClass?: string;
  baseClassActive?: string;
  className?: string;
  children?: React.ReactNode;
  innerRef?: React.Ref<HTMLElement>;
  style?: React.CSSProperties;
  in?: boolean;
  appear?: boolean;
  enter?: boolean;
  exit?: boolean;
  timeout?: number | { appear?: number; enter?: number; exit?: number };
  mountOnEnter?: boolean;
  unmountOnExit?: boolean;
  onEnter?: (...args: unknown[]) => void;
  onEntering?: (...args: unknown[]) => void;
  onEntered?: (...args: unknown[]) => void;
  onExit?: (...args: unknown[]) => void;
  onExiting?: (...args: unknown[]) => void;
  onExited?: (...args: unknown[]) => void;
  [key: string]: unknown;
}

const Fade = (props: FadeProps) => {
  const {
    tag: Tag = 'div',
    baseClass = 'fade',
    baseClassActive = 'show',
    className,
    children,
    innerRef,
    timeout = TransitionTimeouts.Fade,
    appear = true,
    enter = true,
    exit = true,
    in: inProp = true,
    ...otherProps
  } = props;

  const transitionProps = pick({ timeout, appear, enter, exit, in: inProp, ...otherProps }, TransitionPropTypeKeys);
  const childProps = omit(otherProps, TransitionPropTypeKeys);

  const nodeRef = useRef<HTMLElement | null>(null);

  const setRef = (node: HTMLElement | null) => {
    nodeRef.current = node;
    if (typeof innerRef === 'function') {
      innerRef(node);
    } else if (innerRef && typeof innerRef === 'object') {
      (innerRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
  };

  return (
    <TransitionAny {...transitionProps} nodeRef={nodeRef}>
      {(status: TransitionStatus) => {
        const isActive = status === 'entered';
        const classes = classNames(className, baseClass, isActive && baseClassActive);
        return (
          <Tag {...(childProps as object)} className={classes} ref={setRef}>
            {children}
          </Tag>
        );
      }}
    </TransitionAny>
  );
};

export default Fade;
